import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Avatar,
    CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import { addPost, setSelectedPost } from '../../../store/giftcardDesign/action';
import CloseIcon from '@mui/icons-material/Close';
import { uploadWishPostImage } from '../../../apiCalls/urls/urlFunction';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});


const AddPhotoSection = ({ handleSection }) => {
    const dispatch = useDispatch();
    const [selectedImage, setSelectedImage] = useState(null);
    const [preview, setPreview] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState({
        isError: false,
        message: ''
    });

    const handleImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setPreview('');
    };

    const handleUpload = async () => {
        if (error.isError) {
            setError({
                isError: false,
                message: ''
            });
        }

        if (!selectedImage) {
            setError({
                isError: true,
                message: 'please select an image.'
            });
            return;
        };

        setIsUploading(true);
  
        try {
            // Here you would typically upload to a server
            // For now, we'll just create the post object with the preview as image

            // TODO Create form data payload 
            let paylod = new FormData();
            paylod.append('image', selectedImage);

            const response = await uploadWishPostImage(paylod);
            console.log(response)

            if (!response?.status) {
                setError({
                    isError: true,
                    message: response?.message || "Oops! That image couldn’t be uploaded. Please try another one."
                });
                setSelectedImage(null);
                setPreview('');
                return;
            }

           
            const newPost = {
                id: Date.now(),
                image: response?.data?.image,
                altText: "User uploaded image",
                title: selectedImage.name,
                isDeleteable: true,
                isDefault: false,
                isSelected: true,
                isTemplate: false,
            };

            // Dispatch the action to add to Redux store
            dispatch(addPost(newPost));
            dispatch(setSelectedPost(newPost));

            // Reset after successful "upload"
            setSelectedImage(null);
            setPreview('');
        } catch (error) {
            setError({
                isError: true,
                message: error?.message || "Upload failed. Please try uploading a different image."
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: 3,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0,
                    backgroundColor: 'background.main3',
                    width: '100%',
                    maxWidth: 700,
                    mx: 'auto',
                }}
            >
                <Box
                    position={"relative"}
                    flex={1}
                    sx={{
                        width: '100%',
                    }}
                >
                    <Typography variant="h6" component="h2" color={'text'} textAlign={'center'}>
                        Upload Image
                    </Typography>

                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: -24,
                            right: -24,
                            backgroundColor: 'background.main3',
                            borderRadius: 0,
                        }}
                        onClick={() => handleSection('ADDPHOTO')}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {preview ? (
                    <Box sx={{ position: 'relative' }}>
                        <Box
                            component={"img"}
                            src={preview}
                            alt="Preview"
                            sx={{
                                minWidth: 200,
                                width: "100%",
                                height: "auto",
                                maxHeight: 300,
                                borderRadius: 2,
                                objectFit: 'contain'
                            }}
                            variant="rounded"
                        />
                        <IconButton
                            onClick={handleRemoveImage}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'background.paper',
                            }}
                        >
                            <CancelIcon color="error" />
                        </IconButton>
                    </Box>
                ) : (
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={isUploading}
                        sx={{
                            py: 1,
                            maxWidth: 230,
                            color: 'dark.main',
                            backgroundColor: 'background.main3',
                            borderRadius: 1,
                            boxShadow: 0,
                            textTransform: 'capitalize',
                            fontSize: '1.325rem',
                            fontWeight: 400,

                            "&:hover": {
                                backgroundColor: 'background.main3',
                                boxShadow: 3,
                            }
                        }}
                    >
                        Select Image
                        <VisuallyHiddenInput
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                    </Button>
                )}

                {selectedImage && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            Selected: {selectedImage.name}
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleUpload}
                            disabled={isUploading}
                            startIcon={isUploading ? <CircularProgress size={20} /> : null}
                            sx={{ mt: 1 }}
                        >
                            {isUploading ? 'Saving...' : 'Save Image'}
                        </Button>
                    </>
                )}

                {error.isError &&
                    <Typography variant="caption" textAlign="center" sx={{ color: 'red', fontWeight: 500, fontSize: '1rem', textTransform:'capitalize' }}>
                        {error.message}
                    </Typography>
                }

                <Typography variant="caption" textAlign="center" sx={{ color: 'text.primary', fontWeight: 400, fontSize: '0.925rem' }}>
                    Supports JPG, JPEG, PNG. Max size: 5MB
                </Typography>
            </Box>
        </>
    );
};

export default AddPhotoSection;